import LoadingSpinner from 'components/LoadingSpinner/LoadingSpinner';
import WarningInfoIcon from 'components/WarningInfoIcon';
import useRoiAppointment from 'hooks/useRoiAppointment';
import useRoiOpenedWidget from 'hooks/useRoiOpenedWidget';
import useShowRate from 'hooks/useShowRate';
import moment from 'moment';
import { FC } from 'react';
import { APPOINTMENT_STATUS } from 'utils/constants';
import PeriodComparison from '../PeriodComparison/PeriodComparison';
import { TRAFFIC_BY_SOURCE_START_DATE } from '../TrafficBySource/TRAFFIC_SOURCES';

const STAT_DATA = [
  { label: 'Opened Widget' },
  { label: 'Appts Requested' },
  { label: 'Shows' },
];

const OPENED_WIDGET_NOTE = `Opened Widget data is only available from ${moment(
  TRAFFIC_BY_SOURCE_START_DATE
).format('MMM DD, YYYY')} onward.`;

interface StatCardsProps {
  selectedDateRange: { fromDate: string; toDate: string };
  hourType: string;
  clinicIds?: string[];
}

const StatCards: FC<StatCardsProps> = ({ selectedDateRange, hourType, clinicIds }) => {
  const { data: roiNewAppt, isLoading: isRoiNewApptLoading, isValidating: isRoiNewApptValidating } =
    useRoiAppointment({
      startDate: selectedDateRange.fromDate,
      endDate: selectedDateRange.toDate,
      hourType,
      clinicIds,
    });

  const { isLoading: isRoiCompletedApptLoading, isValidating: isRoiCompletedApptValidating } = useRoiAppointment({
    startDate: selectedDateRange.fromDate,
    endDate: selectedDateRange.toDate,
    hourType,
    status: APPOINTMENT_STATUS.COMPLETED.KEY,
    clinicIds,
  });

  const { data: roiOpenedWidget, isLoading: isRoiOpenedWidgetLoading, isValidating: isRoiOpenedWidgetValidating } =
    useRoiOpenedWidget({
      startDate: selectedDateRange.fromDate,
      endDate: selectedDateRange.toDate,
      hourType,
      clinicIds,
    });

  const { data: showRateData, isLoading: isShowRateLoading, isValidating: isShowRateValidating } =
    useShowRate({
      startDate: selectedDateRange.fromDate,
      endDate: selectedDateRange.toDate,
      hourType,
      clinicIds,
    });

  const isLoading =
    isRoiNewApptLoading ||
    isRoiCompletedApptLoading ||
    isRoiOpenedWidgetLoading ||
    isShowRateLoading;

  const isValidating =
    isRoiNewApptValidating ||
    isRoiCompletedApptValidating ||
    isRoiOpenedWidgetValidating ||
    isShowRateValidating;

  const openedWidget = roiOpenedWidget?.periodData.totalOpenedWidget ?? 0;
  const lastOpenedWidget =
    roiOpenedWidget?.lastPeriodData?.totalOpenedWidget ?? 0;

  const showRates = showRateData?.periodData?.showRates ?? 0;
  const lastShowRates = showRateData?.lastPeriodData?.showRates ?? 0;

  const totalAppointments = roiNewAppt?.periodData.totalAppointments ?? 0;

  const lastTotalAppointments =
    roiNewAppt?.lastPeriodData?.totalAppointments ?? 0;

  return (
    <>
      {STAT_DATA.map((stat) => (
        <div
          key={stat.label}
          data-testid={`stat-card-${stat.label.toLowerCase().replace(/ /g, '-')}`}
          className="flex-1 p-2.4 shadow-elevation-06-hover bg-white flex flex-col justify-between  min-h-[10rem]"
        >
          <div className="flex flex-col gap-y-[20.5px]">
            <div className="flex items-center gap-x-0.6">
              <span className="text-18 leading-[2.7rem] font-bold text-darkest-grey">
                {stat.label}
              </span>
              {stat === STAT_DATA[0] && (
                <WarningInfoIcon message={OPENED_WIDGET_NOTE} />
              )}
            </div>
            {(isLoading || isValidating) ? (
              <LoadingSpinner className="all-child:fill-magenta w-4 h-4" />
            ) : (
              <>
                {stat === STAT_DATA[0] && (
                  <span className="text-30 font-bold text-magenta">
                    {openedWidget.toLocaleString()}
                  </span>
                )}
                {stat === STAT_DATA[1] && (
                  <span className="text-30 font-bold text-magenta">
                    {(
                      roiNewAppt?.periodData.totalAppointments ?? 0
                    ).toLocaleString()}
                  </span>
                )}
                {stat === STAT_DATA[2] && (
                  <span className="text-30 font-bold text-magenta">
                    {showRates.toLocaleString()}
                  </span>
                )}
              </>
            )}
          </div>

          {!isLoading && (
            <>
              {stat === STAT_DATA[0] && (
                <PeriodComparison
                  current={openedWidget}
                  last={lastOpenedWidget}
                />
              )}
              {stat === STAT_DATA[1] && (
                <PeriodComparison
                  current={totalAppointments}
                  last={lastTotalAppointments}
                />
              )}
              {stat === STAT_DATA[2] && (
                <PeriodComparison
                  current={showRates}
                  last={lastShowRates}
                />
              )}
            </>
          )}
        </div>
      ))}
    </>
  );
};

export default StatCards;
